/**
 * The coverage notes' sentences in the viewer's language, for the page, the
 * chat and the CSV alike (lookerTileNotes). Literal keys, so the locale
 * integrity test sees them.
 *
 * @module services/looker/lookerCoverageLabels
 */
import type { TFunction } from 'i18next';
import type { CoverageState } from '@/hooks/useLookerCoverage';
import type { CoverageMetric } from './lookerCoverage';
import type { CoverageLineLabels, TileNoteLabels } from './lookerTileNotes';

const METRIC: Record<CoverageMetric, (t: TFunction) => string> = {
  impressions: (t) => t('looker.coverage.metric.impressions'),
  clicks: (t) => t('looker.coverage.metric.clicks'),
  conversions: (t) => t('looker.coverage.metric.conversions'),
  reach: (t) => t('looker.coverage.metric.reach'),
  engagements: (t) => t('looker.coverage.metric.engagements'),
  conversion_value: (t) => t('looker.coverage.metric.conversion_value'),
};

export type CoverageLabels = TileNoteLabels & CoverageLineLabels & { heading: string };

/** What the chat and the CSV need to say the page's coverage notes. */
export interface CoverageNotesInput {
  state: CoverageState;
  labels: CoverageLabels;
  /** The page's channel filter (raw ids); Result badges follow it. Absent / [] = every channel. */
  channels?: readonly string[];
}

export const coverageLabelsFrom = (t: TFunction): CoverageLabels => ({
  heading: t('looker.coverage.heading'),
  notReported: (platform, metric) => t('looker.coverage.notReported', { platform, metric: METRIC[metric](t) }),
  unclassified: t('looker.coverage.unclassified'),
  missingDays: (platform, count, dates) => t('looker.coverage.missingDays', { platform, count, dates }),
  naming: (share, count) => t('looker.coverage.naming', { share, count }),
  namingNone: t('looker.coverage.namingNone'),
  allCurrencies: t('looker.coverage.allCurrencies'),
  complete: t('looker.coverage.complete'),
  idleAll: t('looker.coverage.idleAll'),
  idlePeriod: t('looker.coverage.idlePeriod'),
  failed: t('looker.coverage.failed'),
});
